import React, { useEffect, useState, useCallback } from "react";
import { fetchMatches, recordMatchResult, regenerateBracket, switchTournamentStyle } from "../lib/api";
import { AvatarChip } from "./AvatarChip";
import { BadgeShareModal } from "./BadgeShareModal";

const STYLES = [
  { value: "round-robin", label: "Round Robin" },
  { value: "single-elim", label: "Single Elim" },
  { value: "double-elim", label: "Double Elim" },
  { value: "swiss",       label: "Swiss" },
  { value: "best-of",     label: "Best Of" },
];

function MatchRow({ match, isOwner, onSubmit, busy }) {
  const [a, setA] = useState(match.score_a ?? "");
  const [b, setB] = useState(match.score_b ?? "");

  useEffect(() => {
    setA(match.score_a ?? "");
    setB(match.score_b ?? "");
  }, [match.score_a, match.score_b]);

  const decided = !!match.winner_team_id;
  const winnerA = decided && match.winner_team_id === match.team_a_id;
  const winnerB = decided && match.winner_team_id === match.team_b_id;
  const ready = !!match.team_a && !!match.team_b;
  const canScore = isOwner && ready;

  const submit = (e) => {
    e.preventDefault();
    if (a === "" || b === "") return;
    onSubmit(match.match_id, Number(a), Number(b));
  };

  const teamCard = (t, win) => (
    <div className={`flex items-center gap-2 flex-1 min-w-0 px-3 py-2 rounded-xl border ${
      win ? "border-[#14b8a6] bg-[#14b8a6]/10" : "border-white/10 bg-black/30"
    }`}>
      {t ? (
        <>
          <AvatarChip avatarId={t.avatar_id} size={28} />
          <span className="truncate text-sm font-bold">{t.name}</span>
          <span className="ml-auto text-[10px] text-[#a3a3ac]">MMR {t.mmr ?? "—"}</span>
        </>
      ) : (
        <span className="text-xs text-white/40">— TBD —</span>
      )}
    </div>
  );

  return (
    <div data-testid={`match-row-${match.match_id}`} className="glass rounded-2xl p-3">
      <div className="flex items-center gap-3">
        {teamCard(match.team_a, winnerA)}
        <span className="text-[10px] tracking-widest text-[#14b8a6] font-bold px-1">VS</span>
        {teamCard(match.team_b, winnerB)}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] tracking-widest text-white/50">
          R{match.round} • #{match.slot + 1}
          {decided && <span className="ml-2 text-[#14b8a6]">✓ {match.score_a}–{match.score_b}</span>}
          {decided && (match.mmr_delta_a != null) && (
            <span className="ml-2 text-[#a3a3ac]">
              ΔMMR {match.mmr_delta_a > 0 ? "+" : ""}{match.mmr_delta_a} / {match.mmr_delta_b > 0 ? "+" : ""}{match.mmr_delta_b}
            </span>
          )}
        </span>
        {canScore && (
          <form onSubmit={submit} className="flex items-center gap-1.5">
            <input
              type="number" min="0" placeholder="A"
              value={a}
              onChange={(e) => setA(e.target.value)}
              data-testid={`match-score-a-${match.match_id}`}
              className="w-12 bg-black/40 border border-[#14b8a6]/30 rounded-lg px-2 py-1 text-white text-xs text-center"
            />
            <span className="text-white/40 text-xs">–</span>
            <input
              type="number" min="0" placeholder="B"
              value={b}
              onChange={(e) => setB(e.target.value)}
              data-testid={`match-score-b-${match.match_id}`}
              className="w-12 bg-black/40 border border-[#14b8a6]/30 rounded-lg px-2 py-1 text-white text-xs text-center"
            />
            <button
              type="submit"
              disabled={busy || a === "" || b === ""}
              data-testid={`match-submit-${match.match_id}`}
              className="btn-neon rounded-lg text-[10px] px-3 py-1.5 disabled:opacity-40"
            >
              {decided ? "EDIT" : "SAVE"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function BracketView({ tournament, isOwner, onToast, onTournamentChanged }) {
  const tid = tournament.tournament_id;
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [styleSwitcherOpen, setStyleSwitcherOpen] = useState(false);
  const [unlockedBadges, setUnlockedBadges] = useState([]); // queue of newly-earned badges to popup

  const load = useCallback(async () => {
    try {
      const m = await fetchMatches(tid);
      setMatches(m);
    } catch {
      onToast?.("Could not load bracket.");
    }
  }, [tid, onToast]);

  useEffect(() => { load(); }, [load]);

  const submit = async (mid, sa, sb) => {
    setBusy(true);
    try {
      const r = await recordMatchResult(mid, sa, sb);
      onToast?.(`✅ Score saved • ΔMMR ${r.mmr_delta_a > 0 ? "+" : ""}${r.mmr_delta_a} / ${r.mmr_delta_b > 0 ? "+" : ""}${r.mmr_delta_b}`);
      if (Array.isArray(r.awarded_badges) && r.awarded_badges.length > 0) {
        setUnlockedBadges((q) => [...q, ...r.awarded_badges]);
        onToast?.(`🎖️ Badge unlocked: ${r.awarded_badges.map(b => b.name).join(", ")}`);
      }
      await load();
      onTournamentChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Score save failed.");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!window.confirm("Wipe current results and regenerate the bracket?")) return;
    setBusy(true);
    try {
      await regenerateBracket(tid);
      onToast?.("🔄 Bracket regenerated.");
      await load();
      onTournamentChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Regenerate failed.");
    } finally { setBusy(false); }
  };

  const flipStyle = async (newStyle) => {
    if (newStyle === tournament.style) { setStyleSwitcherOpen(false); return; }
    if (!window.confirm(`Switch to ${newStyle}? Current scores will be wiped.`)) return;
    setBusy(true);
    try {
      await switchTournamentStyle(tid, newStyle);
      onToast?.(`🔀 Switched to ${newStyle}.`);
      setStyleSwitcherOpen(false);
      await load();
      onTournamentChanged?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Switch failed.");
    } finally { setBusy(false); }
  };

  const rounds = matches.reduce((acc, m) => {
    const k = `${m.bracket || "main"}-${m.round}`;
    (acc[k] = acc[k] || []).push(m);
    return acc;
  }, {});
  const roundKeys = Object.keys(rounds).sort();

  if (matches.length === 0) {
    return (
      <div className="glass rounded-2xl p-4 text-center" data-testid="bracket-empty">
        <p className="text-sm text-white/60">No bracket yet — needs at least 2 enrolled teams.</p>
        {isOwner && (
          <button
            onClick={regenerate}
            disabled={busy}
            data-testid="bracket-regenerate-empty"
            className="btn-ghost rounded-xl px-4 py-2 mt-3 text-xs disabled:opacity-50"
          >
            <i className="fas fa-rotate mr-1" /> TRY GENERATE
          </button>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`bracket-${tid}`} className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold">
          🥊 BRACKET • {STYLES.find(s => s.value === tournament.style)?.label || tournament.style}
        </p>
        {isOwner && (
          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => setStyleSwitcherOpen(v => !v)}
              data-testid="bracket-switch-style-btn"
              className="btn-ghost rounded-xl px-3 py-1.5 text-[10px] tracking-widest"
            >
              <i className="fas fa-shuffle mr-1" /> SWITCH STYLE
            </button>
            <button
              onClick={regenerate}
              disabled={busy}
              data-testid="bracket-regenerate-btn"
              className="btn-ghost rounded-xl px-3 py-1.5 text-[10px] tracking-widest disabled:opacity-50"
            >
              <i className="fas fa-rotate mr-1" /> REGEN
            </button>
            {styleSwitcherOpen && (
              <div className="absolute top-full right-0 mt-2 z-50 glass rounded-2xl p-2 min-w-[160px] shadow-2xl pointer-events-auto">
                {STYLES.map(s => (
                  <button
                    key={s.value}
                    onClick={() => flipStyle(s.value)}
                    data-testid={`bracket-style-opt-${s.value}`}
                    className={`block w-full text-left px-3 py-2 rounded-xl text-xs ${
                      s.value === tournament.style ? "bg-[#14b8a6]/20 text-[#14b8a6]" : "hover:bg-white/5 text-white/80"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {roundKeys.map((rk) => {
        const list = rounds[rk];
        const [bracket, roundN] = rk.split("-");
        const bracketLabel =
          bracket === "winners"     ? "WINNERS • " :
          bracket === "losers"      ? "LOSERS • " :
          bracket === "grand_final" ? "GRAND FINAL" :
          bracket === "swiss"       ? "SWISS • " : "";
        const bracketColor =
          bracket === "losers"      ? "text-[#14b8a6]" :
          bracket === "grand_final" ? "text-[#ffe066]" :
          bracket === "swiss"       ? "text-[#14b8a6]" :
          "text-[#14b8a6]";
        return (
          <div key={rk} data-testid={`bracket-round-${rk}`}>
            <p className={`text-[9px] tracking-widest mb-1 ${bracketColor}`}>
              {bracketLabel}{bracket !== "grand_final" ? `ROUND ${roundN}` : ""}
            </p>
            <div className="space-y-2">
              {list.map(m => (
                <MatchRow key={m.match_id} match={m} isOwner={isOwner} onSubmit={submit} busy={busy} />
              ))}
            </div>
          </div>
        );
      })}
      {/* Auto-pop badge share modal as users earn them — one at a time. */}
      <BadgeShareModal
        badge={unlockedBadges[0]}
        leagueName={null}
        onClose={() => setUnlockedBadges((q) => q.slice(1))}
        onToast={onToast}
      />
    </div>
  );
}

export default BracketView;
